// universal-chart.component.ts
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule,
} from 'ng-apexcharts';

export interface ChartOptions {
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  dataLabels: ApexDataLabels;
  stroke: ApexStroke;
  yaxis: ApexYAxis;
  legend: ApexLegend;
  grid: ApexGrid;
  tooltip: ApexTooltip;
  colors: string[];
  plotOptions: ApexPlotOptions;
  responsive?: ApexResponsive[];
  annotations?: any;
  labels?: string[];
}

@Component({
  standalone: true,
  selector: 'app-universal-chart',
  templateUrl: './universal-chart.component.html',
  styleUrls: ['./universal-chart.component.scss'],
  imports: [NgApexchartsModule],
})
export class UniversalChartComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('chartContainer') chartContainer!: ElementRef;
  private containerWidth = 0;
  private resizeObserver: ResizeObserver | null = null;

  public defaultOptions: ChartOptions = {
    series: [{ name: '', data: [0] }],
    chart: {
      id: 'realtime',
      type: 'bar',
      height: 'auto',
      background: '#404040',
      stacked: true,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    colors: ['#2CC3E8', '#50B748'],
    dataLabels: {
      enabled: false,
      style: { fontSize: '13px', fontWeight: 700, colors: ['#50B748'] },
    },
    stroke: { width: 0 },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '45px',
        borderRadius: 2,
        dataLabels: { position: 'top' },
      },
    },
    xaxis: {
      categories: [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ],
      labels: { style: { colors: '#fff', fontSize: '12px' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      max: 40,
      tickAmount: 4,
      labels: {
        style: { colors: '#888', fontSize: '12px' },
        formatter: (val: number) => Math.round(val).toString(),
      },
      forceNiceScale: true,
      decimalsInFloat: 0,
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
      labels: { colors: '#fff' },
      itemMargin: { horizontal: 12, vertical: 6 },
      markers: { shape: 'circle', strokeWidth: 0 },
    },
    grid: {
      show: true,
      borderColor: '#fff',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    tooltip: {
      enabled: true,
      theme: 'dark',
      x: { formatter: (val) => val.toString() },
      y: { formatter: (val) => val.toString() },
    },
  };

  public chartOptions: ChartOptions = { ...this.defaultOptions };
  private _options: Partial<ChartOptions> | null = null;

  @Input() set options(value: Partial<ChartOptions>) {
    this._options = value;
    if (this.chartContainer) {
      setTimeout(() => this.buildChart(), 0);
    }
  }

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.containerWidth = this.chartContainer?.nativeElement?.offsetWidth || 0;

    // Setup resize observer to handle container size changes
    if (this.chartContainer) {
      this.resizeObserver = new ResizeObserver(() => {
        this.containerWidth = this.chartContainer.nativeElement.offsetWidth;
        if (this._options) {
          this.buildChart();
        }
      });
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }

    if (this._options) {
      setTimeout(() => this.buildChart(), 0);
    }
  }

  private buildChart() {
    if (!this._options) return;

    const chartType =
      this._options?.chart?.type ?? this.defaultOptions.chart.type;
    const isPieChart =
      chartType === 'pie' || chartType === 'donut' || chartType === 'radialBar';

    if (isPieChart) {
      this.chartOptions = {
        ...this.defaultOptions,
        ...this._options,
        labels: this._options?.labels ?? [],
        series: this._options?.series ?? [],
        chart: {
          ...this.defaultOptions.chart,
          ...this._options?.chart,
        },
      } as ChartOptions;
    } else {
      this.chartOptions = {
        series: this._options?.series ?? this.defaultOptions.series,
        chart: {
          ...this.defaultOptions.chart,
          ...this._options?.chart, // ← width from caller is preserved here
          type: this._options?.chart?.type ?? this.defaultOptions.chart.type,
        },
        colors: this._options?.colors ?? this.defaultOptions.colors,
        dataLabels: {
          ...this.defaultOptions.dataLabels,
          ...this._options?.dataLabels,
        },
        stroke: {
          ...this.defaultOptions.stroke,
          ...this._options?.stroke,
        },
        plotOptions: {
          ...this.defaultOptions.plotOptions,
          ...this._options?.plotOptions,
          bar: {
            ...this.defaultOptions.plotOptions?.bar,
            ...this._options?.plotOptions?.bar,
          },
        },
        xaxis: {
          ...this.defaultOptions.xaxis,
          ...this._options?.xaxis,
          labels: {
            ...this.defaultOptions.xaxis?.labels, // ← defaults first
            ...this._options?.xaxis?.labels, // ← caller wins
          },
        },
        yaxis: {
          ...this.defaultOptions.yaxis,
          ...this._options?.yaxis,
          labels: {
            ...this.defaultOptions.yaxis?.labels,
            ...this._options?.yaxis?.labels,
          },
        },
        legend: {
          ...this.defaultOptions.legend,
          ...this._options?.legend,
          labels: {
            ...this.defaultOptions.legend?.labels,
            ...this._options?.legend?.labels,
          },
        },
        grid: {
          ...this.defaultOptions.grid,
          ...this._options?.grid,
        },
        tooltip: {
          ...this.defaultOptions.tooltip,
          ...this._options?.tooltip,
        },
        responsive: this._options?.responsive ?? this.defaultOptions.responsive,
      };
    }

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}
